import { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { getAttachmentBlob } from "../../api/attachments";
import { useInvoices } from "../../api/lists";
import { useAttachments } from "../../hooks/useAttachments";
import { useConfig, useImports } from "../../api/queries";
import { useRegistry } from "../../context/RegistryContext";
import { NewImportModal } from "../Admin/ImportsPage/components/NewImportModal";
import { DateDisplay } from "../../components/DateDisplay";
import { statementParser } from "../../logic/statement-parsers";
import { extractPdfText } from "../../utils/pdfText";
import { invoiceSender } from "../../logic/invoice-sender";
import type {
  ImportConfigDto,
  ImportResultDto,
  InvoiceEntity,
} from "../../api/types";

export type StatementInvoiceWarningsProps = {
  /** Forwarded from the import dialog, which closes as soon as it's done. */
  onImported?: (result?: ImportResultDto) => void;
};

type Pending = {
  invoice: InvoiceEntity;
  importConfig: ImportConfigDto;
};

/**
 * A card statement invoice carries the period's complete transaction list, but
 * only once it's imported. Surface the invoices that still hold one — the step
 * is deliberately manual (it closes the period to further statement uploads),
 * so an un-imported one is exactly the kind of thing the inbox is for.
 */
export const StatementInvoiceWarnings = ({
  onImported,
}: StatementInvoiceWarningsProps) => {
  const { data: invoices } = useInvoices();
  const { data: imports } = useImports();
  const { data: config } = useConfig();
  const { data: attachments } = useAttachments();
  // A hidden account's statement still has to be imported.
  const { withHidden } = useRegistry();

  const [preparing, setPreparing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<{
    file: File;
    invoice: InvoiceEntity;
    importConfig: ImportConfigDto;
  } | null>(null);

  // Importers whose invoice we can read at all.
  const statementImporters = (config?.importConfigs ?? []).filter(
    (c) => c.invoiceParser && c.source,
  );

  // An invoice is pending when it is from one of the user's accounts, that
  // account has such an importer, and no import has been linked to it yet. A
  // payee's invoice never qualifies: an import can't be linked to one.
  const pending: Pending[] = (invoices ?? []).flatMap((invoice) => {
    const sender = invoiceSender(invoice, withHidden);
    if (sender?.kind !== "source" || !sender.entity) return [];
    const sourceName = sender.entity.name;
    const importConfig = statementImporters.find(
      (c) => c.source === sourceName,
    );
    if (!importConfig) return [];
    const alreadyImported = (imports ?? []).some(
      (imp) => imp.invoiceId === invoice.id && !imp.deletedAt,
    );
    return alreadyImported ? [] : [{ invoice, importConfig }];
  });

  if (pending.length === 0 && !upload) return null;

  // Read the invoice's PDF, parse its specification and hand the resulting CSV
  // to the normal import flow — including its overlap → update → review path,
  // which is how the invoice merges into the month's existing statement import.
  const prepare = async ({ invoice, importConfig }: Pending) => {
    const attachmentId = invoice.attachments?.[0];
    if (!attachmentId) {
      setError(
        `"${invoice.name}" has no attached PDF to read the transactions from.`,
      );
      return;
    }
    // The file is fetched by its versioned URL, so the attachment's metadata
    // is needed, not just its id.
    const attachment = attachments?.find((a) => a.id === attachmentId);
    if (!attachment) {
      setError(
        `The PDF attached to "${invoice.name}" isn't loaded yet — try again in a moment.`,
      );
      return;
    }
    const parse = statementParser(importConfig.invoiceParser ?? "");
    if (!parse) {
      setError(`No parser for ${importConfig.invoiceParser}.`);
      return;
    }

    setPreparing(invoice.id);
    setError(null);
    try {
      const csv = parse(
        await extractPdfText(await getAttachmentBlob(attachment)),
      );
      setUpload({
        file: new File([csv], `${invoice.name}.csv`, { type: "text/csv" }),
        invoice,
        importConfig,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreparing(null);
    }
  };

  return (
    <>
      {pending.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>
            {pending.length === 1
              ? "1 invoice still holds its transactions"
              : `${pending.length} invoices still hold their transactions`}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These card invoices list every transaction of their period.
            Importing one fills in the references and details the bank's
            statement export leaves out, and closes the period to further
            statement uploads.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={0.5}>
            {pending.map(({ invoice, importConfig }) => (
              <Box
                key={invoice.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Link
                  component={Box}
                  underline="none"
                  sx={{ flexGrow: 1, color: "inherit" }}
                >
                  <Typography variant="body2" component="span">
                    {invoice.name}
                  </Typography>{" "}
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                  >
                    (<DateDisplay date={invoice.dateReceived} />)
                  </Typography>
                </Link>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={preparing !== null}
                  startIcon={
                    preparing === invoice.id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : undefined
                  }
                  onClick={() => void prepare({ invoice, importConfig })}
                >
                  Import transactions
                </Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {upload && (
        <NewImportModal
          // Remount per invoice so the preloaded file seeds fresh state.
          key={upload.invoice.id}
          open
          initialFile={upload.file}
          initialImportType={upload.importConfig.name}
          initialInvoiceId={upload.invoice.id}
          finalize
          intro={
            <Alert severity="info" sx={{ mb: 2 }}>
              Transactions read from <strong>{upload.invoice.name}</strong>. If
              this period was already imported from a statement, you'll be
              offered the existing import to update — that's the one to pick.
            </Alert>
          }
          onClose={() => setUpload(null)}
          onImported={(result) => {
            setUpload(null);
            onImported?.(result);
          }}
        />
      )}
    </>
  );
};
